import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { PortalShell } from "@/layouts/PortalShell";
import { DashboardRightSidebar } from "@/components/dashboard/DashboardRightSidebar";
import { ContactModal } from "@/components/ContactModal";
import { WelcomeModal, getWelcomeSeen } from "@/components/WelcomeModal";
import { PageContainer } from "@/components/wizard/PageContainer";
import { isHealthSource } from "@/lib/brand";

function resetDashboardScrollContainers(mainEl: HTMLElement | null) {
  window.scrollTo({ top: 0, left: 0 });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  mainEl?.scrollTo({ top: 0, left: 0 });
  mainEl
    ?.querySelectorAll<HTMLElement>("[data-page-container-scroll]")
    .forEach((el) => el.scrollTo({ top: 0, left: 0 }));
}

/** Prefer mobile menu; on md+ layout the hamburger is display:none so focus first desktop nav link. */
function focusDashboardHeaderNav() {
  const mobile = document.querySelector<HTMLElement>(
    '[aria-label="Open navigation menu"]'
  );
  if (mobile && mobile.offsetParent != null) {
    mobile.focus({ preventScroll: true });
    return;
  }
  document
    .querySelector<HTMLElement>(
      'header[aria-label="Site header"] nav[aria-label="Main"] a'
    )
    ?.focus({ preventScroll: true });
}

export function DashboardLayout() {
  const location = useLocation();
  const [contactOpen, setContactOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const isDashboardHome = location.pathname === "/";
  // The feature image is a HealthSource ad; other brands give the dashboard the full width.
  const showFeatureImage = isDashboardHome && isHealthSource();
  const mainRef = useRef<HTMLElement>(null);
  const prevWelcomeOpenRef = useRef<boolean | null>(null);

  // Only greet first-time users on the dashboard home — never over a child route such as
  // /form-handler (which lives under this layout). Intake wizard steps render under ComplaintLayout,
  // so before this guard a forms-first session would pop the welcome over the first form.
  useEffect(() => {
    if (isDashboardHome && !getWelcomeSeen()) setWelcomeOpen(true);
  }, [isDashboardHome]);

  /**
   * Closing the welcome modal unmounts its primary button; focus then moves to the
   * next tabbable in DOM order (often the footer “Contact Us”), which triggers
   * scroll-into-view and leaves small viewports scrolled away from the header.
   */
  useLayoutEffect(() => {
    const prev = prevWelcomeOpenRef.current;
    prevWelcomeOpenRef.current = welcomeOpen;
    if (prev !== true || welcomeOpen !== false) return;

    const run = () => {
      resetDashboardScrollContainers(mainRef.current);
      focusDashboardHeaderNav();
    };
    run();
    const id1 = window.requestAnimationFrame(run);
    const id2 = window.requestAnimationFrame(() => {
      resetDashboardScrollContainers(mainRef.current);
      focusDashboardHeaderNav();
    });
    const t = window.setTimeout(run, 120);
    return () => {
      window.cancelAnimationFrame(id1);
      window.cancelAnimationFrame(id2);
      window.clearTimeout(t);
    };
  }, [welcomeOpen]);

  return (
    <PortalShell onContactClick={() => setContactOpen(true)}>
      <main
        ref={mainRef}
        className="content-body flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-wrap gap-4 overflow-y-auto overflow-x-clip px-4 py-2.5 pb-4 min-[400px]:gap-5 min-[400px]:py-3 min-[500px]:gap-6 min-[500px]:px-5 min-[500px]:pb-5 min-[600px]:px-6 min-[600px]:py-3.5"
        role="main"
        style={{ maxWidth: 1400, margin: "0 auto", width: "100%" }}
      >
        <div
          className={
            showFeatureImage
              ? "min-w-0 flex-1 flex flex-col min-h-0 lg:flex-[0_0_65%]"
              : "min-w-0 flex-1 flex flex-col min-h-0"
          }
        >
          <PageContainer>
            <Outlet />
          </PageContainer>
        </div>
        {showFeatureImage && (
          <aside
            className="hidden min-w-0 flex-1 flex-col lg:flex lg:flex-[0_0_calc(35%-24px)] lg:self-start"
            aria-label="Clinic feature image"
          >
            <DashboardRightSidebar />
          </aside>
        )}
      </main>
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
      <WelcomeModal open={welcomeOpen} onClose={() => setWelcomeOpen(false)} />
    </PortalShell>
  );
}
